<script setup lang="ts">
import {
  getDate,
  getTime,
  getShortMonth,
  getDayOfWeek,
} from "../modules/helpers/day";
import { useUserStore } from "../stores";

const { $get } = useAxios();

const emit = defineEmits(["update:onPopupOpen"]);

const route = useRoute();
const router = useRouter();

const userStore = useUserStore();
const user = computed(() => userStore.user);

const performId = computed(() => route?.params?.perform);

if (!performId.value) throw new Error("unknown perform");
let poster = route?.params?.poster;

const showHall = ref(false);

const isPopupOpen = ref(false);

const { data: perform } = await useAsyncData(
  `get perform with id - ${performId.value}`,
  () => $get(`/api/v3/perform/${performId.value}`)
);

const ids = [performId.value];
let performs = ids.reduce((result, id) => {
  return (result += `perform_in=${id}&`);
}, "");

const limit = 20;

const { data: _posters } = await useAsyncData(
  `get posters by perform id - ${performs}`,
  () =>
    $get(
      `/api/v3/poster?${performs}isActive=true&_sort=date:asc&date_gt=${new Date(
        new Date(Date.now()).setHours(new Date(Date.now()).getHours() - 1)
      ).toISOString()}&_limit=${limit}`
    )
);

const selectedPoster = computed({
  get() {
    if (_posters.value) {
      if (!poster) return _posters.value[0];
      else {
        return _posters.value.find((el) => el._id === poster);
      }
    }
  },
  async set(value) {
    await navigateTo(`/buy/${performId.value}/${value._id}`);
  },
});

const loaded = ref(false);

onMounted(async () => {
  loaded.value = true;
});

const hall = computed(() => {
  if (selectedPoster.value) {
    return selectedPoster.value.hall;
  }
  return null;
});

const hallCathegory = computed(() => {
  if (hall.value) {
    return hall.value.cathegory;
  }
  return null;
});

const posters = computed(() => {
  const data = new Map();
  _posters.value.forEach((poster) => {
    data.set(
      `${getDate(poster.date)} ${getShortMonth(poster.date)} ${getTime(
        poster.date
      )}`,
      poster
    );
  });
  return data;
});

function onPosterSelect(e) {
  selectedPoster.value = posters.value.get(e);
}
</script>

<template>
  <div v-if="_posters && perform && loaded">
    <r-section title="Покупка билета" v-if="performId && perform">
      <div class="grid grid-cols-12 gap-7" v-if="_posters.length">
        <div
          class="col-start-1 col-end-13 lg:col-end-5 h-68 rounded-xl overflow-hidden"
        >
          <img
            loading="lazy"
            class="w-full h-full object-cover"
            :src="`https://ucarecdn.com/${perform.img.fileKey}/-/format/auto/-/quality/smart/image.png`"
            :alt="perform.name"
          />
        </div>
        <div
          class="col-start-1 lg:col-start-5 col-end-13 flex flex-col justify-between space-y-7 lg:space-y-0"
        >
          <div>
            <div class="flex flex-row space-x-7">
              <h2
                class="font-sans text-4xl text-dark-gray leading-none my-auto"
              >
                {{ perform.name }}
              </h2>
              <r-button
                v-if="
                  selectedPoster.kind === 'link' || selectedPoster.isTouring
                "
                size="sm"
                defaultColor="dark-gray"
                class="my-auto text-sm"
                cursor="default"
                >{{
                  selectedPoster.tourInfo.length
                    ? selectedPoster.tourInfo
                    : "Гастроли"
                }}</r-button
              >
              <r-button
                v-else-if="perform.isPremiere"
                size="sm"
                class="my-auto text-sm"
                cursor="default"
                >Премьера
              </r-button>
            </div>

            <p class="font-sans font-regular text-akcent text-">
              {{ perform.genre }}
            </p>
          </div>

          <div class="flex flex-row space-x-7">
            <div
              class="flex flex-row space-x-2 justify-center justify-items-center"
            >
              <div>
                <svg
                  id="time"
                  xmlns="http://www.w3.org/2000/svg"
                  width="42"
                  height="42"
                  viewBox="0 0 41.439 41.576"
                >
                  <path
                    id="Path_138"
                    data-name="Path 138"
                    d="M2.739.138,28.3-.02a2.56,2.56,0,0,1,2.569,2.594l-.158,25.811a2.637,2.637,0,0,1-2.6,2.626L2.55,31.17A2.56,2.56,0,0,1-.02,28.575L.138,2.764A2.637,2.637,0,0,1,2.739.138Z"
                    transform="matrix(-0.899, 0.438, -0.438, -0.899, 41.413, 28.024)"
                    fill="#393939"
                  />
                  <g id="clock" transform="translate(12 11.788)">
                    <g
                      id="Group_88"
                      data-name="Group 88"
                      transform="translate(8.303 4.176)"
                    >
                      <g id="Group_87" data-name="Group 87">
                        <path
                          id="Path_150"
                          data-name="Path 150"
                          d="M240.073,125.193l-2.51-1.882v-3.834a.7.7,0,0,0-1.394,0v4.183a.7.7,0,0,0,.279.558l2.789,2.091a.7.7,0,0,0,.837-1.115Z"
                          transform="translate(-236.169 -118.779)"
                          fill="#fff"
                        />
                      </g>
                    </g>
                    <g id="Group_90" data-name="Group 90">
                      <g id="Group_89" data-name="Group 89">
                        <path
                          id="Path_151"
                          data-name="Path 151"
                          d="M9,0a9,9,0,1,0,9,9A9.01,9.01,0,0,0,9,0ZM9,16.606A7.606,7.606,0,1,1,16.606,9,7.615,7.615,0,0,1,9,16.606Z"
                          fill="#fff"
                        />
                      </g>
                    </g>
                  </g>
                </svg>
              </div>
              <div class="my-auto h-5 pt-px">
                <p class="font-sans font-bold text-dark-gray text-base">
                  {{ perform.time }}
                </p>
              </div>
            </div>
          </div>
          <r-multy-select
            title="Выберите дату"
            class="flex flex-col relative"
            :cols="4"
            type="flex"
            :size="'w-42'"
            :options="[...posters.keys()]"
            @r:change="onPosterSelect"
            :is-multiple="false"
            :selected="`${getDate(selectedPoster.date)} ${getShortMonth(
              selectedPoster.date
            )} ${getTime(selectedPoster.date)}`"
          ></r-multy-select>
        </div>
        <!-- <div class="col-start-1 col-end-13 grid gap-12 relative">
        <h1>
          Онлайн продажа билетов временно не доступна. Обратитесь пожалуйста в
          кассу театра
        </h1>
      </div> -->

        <div class="col-start-1 col-end-13 grid gap-12 relative">
          <RCreateOrder :poster="selectedPoster"></RCreateOrder>
        </div>
      </div>
    </r-section>
    <!-- <ErrorPage v-else /> -->
  </div>
  <RFullPageProLoader v-else />
</template>
